import { useState, useEffect } from "react";
import axios from "axios";
import { X, FileText, Sparkles, Globe, TrendingUp, RefreshCw, CheckCircle, ListChecks } from "lucide-react";
import { toast } from "sonner";

/**
 * Subpages-Auswahl-Modal: Zeigt aggregierte Vorschläge aus 3 Quellen
 * (Branche / Crawl / All-Time-Popular) als Toggle-Liste. Vertriebler kann
 * pro Subpage entscheiden ob sie generiert werden soll.
 *
 * Wird vor „Neu erstellen" aus dem Settings-Panel geöffnet.
 */
const SOURCE_INFO = {
  branche:   { icon: Sparkles,    label: "Branchen-Profil",    color: "text-violet-700 dark:text-violet-300",   bg: "bg-violet-500/10 border-violet-500/30" },
  crawl:     { icon: Globe,       label: "Aus alter Website",  color: "text-emerald-700 dark:text-emerald-300", bg: "bg-emerald-500/10 border-emerald-500/30" },
  popular:   { icon: TrendingUp,  label: "Häufig verwendet",   color: "text-amber-700 dark:text-amber-300",     bg: "bg-amber-500/10 border-amber-500/30" },
};

export const SubpageSuggestionsModal = ({ open, onClose, projectId, API, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [industryLabel, setIndustryLabel] = useState("");
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/projects/${projectId}/subpage-suggestions`, { withCredentials: true })
      .then(res => {
        if (cancelled) return;
        const list = res.data.suggestions || [];
        setSuggestions(list);
        setIndustryLabel(res.data.industry_label || "");
        // Default: alle Branchen-Vorschläge + Crawl pre-selektiert; popular off
        const def = new Set();
        list.forEach((sp, i) => {
          if (sp.already_exists) return;
          if (sp.source === "branche" || sp.source === "crawl") def.add(i);
        });
        setSelected(def);
      })
      .catch(err => {
        if (!cancelled) toast.error(err.response?.data?.detail || "Vorschläge laden fehlgeschlagen");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, projectId, API]);

  if (!open) return null;

  const toggle = (i) => {
    setSelected(s => {
      const next = new Set(s);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(suggestions.map((_, i) => i).filter(i => !suggestions[i].already_exists)));
  const selectNone = () => setSelected(new Set());
  const selectBySource = (src) => {
    const next = new Set(selected);
    suggestions.forEach((sp, i) => {
      if (sp.source === src && !sp.already_exists) next.add(i);
    });
    setSelected(next);
  };

  const handleConfirm = () => {
    const picked = Array.from(selected).map(i => {
      const sp = suggestions[i];
      return {
        title: sp.title,
        brief: sp.brief || "",
        page_type: sp.page_type || "",
        in_nav: sp.in_nav !== false,
      };
    });
    onConfirm?.(picked);
    onClose();
  };

  const counts = {
    branche: suggestions.filter(s => s.source === "branche" && !s.already_exists).length,
    crawl:   suggestions.filter(s => s.source === "crawl" && !s.already_exists).length,
    popular: suggestions.filter(s => s.source === "popular" && !s.already_exists).length,
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="subpage-suggestions-modal">
      <div className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-border flex items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-base flex items-center gap-2">
              <ListChecks size={18} /> Welche Unterseiten sollen mit gebaut werden?
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              Wähle aus {suggestions.length} Vorschlägen{industryLabel && <> für <strong>{industryLabel}</strong></>} aus 3 Quellen. Du kannst auch erst die Demo bauen und Subpages später nachträglich erzeugen.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-sm hover:bg-muted transition-colors" data-testid="subpage-suggestions-close">
            <X size={16} />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center p-10">
            <RefreshCw size={20} className="animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="px-5 py-2 border-b border-border bg-muted/20 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mr-1">Quick:</span>
              <button type="button" onClick={selectAll} className="text-xs px-2 py-1 rounded-sm bg-card border border-border hover:bg-muted transition-colors" data-testid="quick-select-all">
                Alle ✓
              </button>
              <button type="button" onClick={selectNone} className="text-xs px-2 py-1 rounded-sm bg-card border border-border hover:bg-muted transition-colors" data-testid="quick-select-none">
                Keine
              </button>
              {counts.branche > 0 && (
                <button type="button" onClick={() => selectBySource("branche")} className="text-xs px-2 py-1 rounded-sm bg-violet-500/10 text-violet-700 dark:text-violet-300 border border-violet-500/30 hover:bg-violet-500/20 transition-colors" data-testid="quick-select-branche">
                  + {counts.branche} Branchen-Vorschläge
                </button>
              )}
              {counts.crawl > 0 && (
                <button type="button" onClick={() => selectBySource("crawl")} className="text-xs px-2 py-1 rounded-sm bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors" data-testid="quick-select-crawl">
                  + {counts.crawl} aus Crawl
                </button>
              )}
              {counts.popular > 0 && (
                <button type="button" onClick={() => selectBySource("popular")} className="text-xs px-2 py-1 rounded-sm bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30 hover:bg-amber-500/20 transition-colors" data-testid="quick-select-popular">
                  + {counts.popular} populäre
                </button>
              )}
              <span className="ml-auto text-xs font-semibold" data-testid="selected-count">
                {selected.size} ausgewählt
              </span>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {suggestions.length === 0 && (
                <div className="text-center py-12 text-sm text-muted-foreground">
                  Keine Vorschläge gefunden — du kannst Subpages später manuell hinzufügen.
                </div>
              )}
              {suggestions.map((sp, i) => {
                const SI = SOURCE_INFO[sp.source] || SOURCE_INFO.branche;
                const Icon = SI.icon;
                const isSelected = selected.has(i);
                const disabled = sp.already_exists;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => !disabled && toggle(i)}
                    disabled={disabled}
                    className={`w-full text-left p-3 rounded-md border transition-all flex items-start gap-3 ${
                      disabled
                        ? "border-emerald-500/30 bg-emerald-500/5 opacity-70 cursor-default"
                        : isSelected
                          ? "border-foreground/40 bg-muted/30 shadow-sm"
                          : "border-border bg-card hover:border-foreground/20"
                    }`}
                    data-testid={`subpage-suggestion-${i}`}
                  >
                    <div className="shrink-0 mt-0.5">
                      <div className={`w-5 h-5 rounded-sm border-2 flex items-center justify-center ${
                        disabled
                          ? "border-emerald-600 bg-emerald-600 text-white"
                          : isSelected
                            ? "border-foreground bg-foreground text-background"
                            : "border-border bg-card"
                      }`}>
                        {(isSelected || disabled) && <CheckCircle size={11} />}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{sp.title}</span>
                        <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm border ${SI.bg} ${SI.color}`}>
                          <Icon size={9} /> {SI.label}
                        </span>
                        {sp.count > 1 && (
                          <span className="text-[10px] text-muted-foreground">{sp.count}× verwendet</span>
                        )}
                        {disabled && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
                            ✓ existiert bereits
                          </span>
                        )}
                        {sp.already_queued && !disabled && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-500/10 text-amber-700 dark:text-amber-300">
                            in Queue
                          </span>
                        )}
                      </div>
                      {sp.brief && (
                        <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{sp.brief}</p>
                      )}
                    </div>
                    <FileText size={14} className="text-muted-foreground/40 shrink-0 mt-1" />
                  </button>
                );
              })}
            </div>

            <div className="px-5 py-3 border-t border-border bg-muted/20 flex items-center justify-between gap-3">
              <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 text-muted-foreground hover:text-foreground transition-colors" data-testid="subpage-suggestions-cancel">
                Abbrechen
              </button>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => { onConfirm?.([]); onClose(); }} className="text-xs px-3 py-1.5 rounded-md border border-border hover:bg-muted transition-colors" data-testid="subpage-suggestions-skip">
                  Ohne Subpages bauen
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={selected.size === 0}
                  className="text-xs font-semibold px-4 py-1.5 rounded-md bg-foreground text-background hover:opacity-90 transition disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="subpage-suggestions-confirm"
                >
                  {selected.size > 0 ? `${selected.size} Subpage${selected.size === 1 ? "" : "s"} mit bauen →` : "Bitte mind. 1 auswählen"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
